import { openDB, type IDBPDatabase } from 'idb';
import type { DbExport } from '../types';

export interface ProfileMeta {
  name: string;
  /** Monotonic change counter — file mtime in dev, write counter in IndexedDB. */
  mtime: number;
}

export interface ProfileStorage {
  /** Diagnostic identifier. */
  readonly kind: 'file' | 'indexeddb';
  /** True if external mutators (e.g. the CLI) can change data and we should poll. */
  readonly polls: boolean;

  listProfiles(): Promise<ProfileMeta[]>;
  /** Returns null if the profile does not exist yet. */
  readProfile(name: string): Promise<DbExport | null>;
  writeProfile(name: string, data: DbExport): Promise<void>;
  deleteProfile(name: string): Promise<void>;
  duplicateProfile(from: string, to: string): Promise<void>;
  /** Cheap call meant for polling. */
  pollMeta(): Promise<ProfileMeta[]>;
}

let cached: ProfileStorage | null = null;
let detecting: Promise<ProfileStorage> | null = null;

/**
 * Returns the active storage backend. Detected once per page load:
 * if the dev profile API answers, use the file-backed backend; otherwise
 * fall back to IndexedDB.
 */
export async function getStorage(): Promise<ProfileStorage> {
  if (cached) return cached;
  if (detecting) return detecting;
  detecting = detect().then(s => {
    cached = s;
    detecting = null;
    return s;
  });
  return detecting;
}

async function detect(): Promise<ProfileStorage> {
  try {
    const r = await fetch('/api/profiles', { cache: 'no-store' });
    // A 200 alone isn't enough: static hosts (Cloudflare Pages, Netlify,
    // GitHub Pages configured for SPA) rewrite unknown paths to index.html,
    // so this call comes back as `200 OK / text/html`. Committing to the
    // file backend on that response would strand the app on the first
    // `listProfiles().json()` call. Require a JSON content-type as proof
    // the dev API actually answered.
    const ct = r.headers.get('content-type') ?? '';
    if (r.ok && ct.includes('application/json')) return new FileBackedStorage();
  } catch {
    /* server not running */
  }
  return new IndexedDbStorage();
}

// ── File-backed (dev server) ────────────────────────────────────────────────

class FileBackedStorage implements ProfileStorage {
  readonly kind = 'file' as const;
  readonly polls = true;

  async listProfiles(): Promise<ProfileMeta[]> {
    const r = await fetch('/api/profiles', { cache: 'no-store' });
    if (!r.ok) throw new Error(`listProfiles failed: ${r.status}`);
    const body = (await r.json()) as { profiles: ProfileMeta[] };
    return body.profiles ?? [];
  }

  async readProfile(name: string): Promise<DbExport | null> {
    const r = await fetch(`/api/profiles/${encodeURIComponent(name)}`, { cache: 'no-store' });
    if (r.status === 404) return null;
    if (!r.ok) throw new Error(`readProfile(${name}) failed: ${r.status}`);
    return (await r.json()) as DbExport;
  }

  async writeProfile(name: string, data: DbExport): Promise<void> {
    const r = await fetch(`/api/profiles/${encodeURIComponent(name)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data, null, 2),
    });
    if (!r.ok) throw new Error(`writeProfile(${name}) failed: ${r.status}`);
  }

  async deleteProfile(name: string): Promise<void> {
    const r = await fetch(`/api/profiles/${encodeURIComponent(name)}`, { method: 'DELETE' });
    if (!r.ok && r.status !== 404) throw new Error(`deleteProfile(${name}) failed: ${r.status}`);
  }

  async duplicateProfile(from: string, to: string): Promise<void> {
    const r = await fetch(
      `/api/profiles/${encodeURIComponent(from)}/copy?to=${encodeURIComponent(to)}`,
      { method: 'POST' },
    );
    if (!r.ok) throw new Error(`duplicateProfile(${from}→${to}) failed: ${r.status}`);
  }

  async pollMeta(): Promise<ProfileMeta[]> {
    return this.listProfiles();
  }
}

// ── IndexedDB-backed (production / static deploy) ───────────────────────────
//
// Single DB `cairn` with one object store `profiles` keyed by name. Each row
// holds the entire DbExport plus a monotonic mtime that ticks on every write
// (used so the polling layer in dev can be uniform across backends).

const IDB_NAME = 'cairn';
const IDB_VERSION = 1;

interface ProfileRow {
  name: string;
  mtime: number;
  data: DbExport;
}

class IndexedDbStorage implements ProfileStorage {
  readonly kind = 'indexeddb' as const;
  readonly polls = false;

  private dbp: Promise<IDBPDatabase> | null = null;

  private getDb(): Promise<IDBPDatabase> {
    if (!this.dbp) {
      this.dbp = openDB(IDB_NAME, IDB_VERSION, {
        upgrade(db) {
          if (!db.objectStoreNames.contains('profiles')) {
            db.createObjectStore('profiles', { keyPath: 'name' });
          }
        },
      });
    }
    return this.dbp;
  }

  async listProfiles(): Promise<ProfileMeta[]> {
    const db = await this.getDb();
    const rows = (await db.getAll('profiles')) as ProfileRow[];
    return rows
      .map(r => ({ name: r.name, mtime: r.mtime }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  async readProfile(name: string): Promise<DbExport | null> {
    const db = await this.getDb();
    const row = (await db.get('profiles', name)) as ProfileRow | undefined;
    return row ? row.data : null;
  }

  async writeProfile(name: string, data: DbExport): Promise<void> {
    const db = await this.getDb();
    const existing = (await db.get('profiles', name)) as ProfileRow | undefined;
    const row: ProfileRow = {
      name,
      mtime: (existing?.mtime ?? 0) + 1,
      data,
    };
    await db.put('profiles', row);
  }

  async deleteProfile(name: string): Promise<void> {
    const db = await this.getDb();
    await db.delete('profiles', name);
  }

  async duplicateProfile(from: string, to: string): Promise<void> {
    const src = await this.readProfile(from);
    if (!src) throw new Error(`Profile not found: ${from}`);
    await this.writeProfile(to, src);
  }

  async pollMeta(): Promise<ProfileMeta[]> {
    return this.listProfiles();
  }
}
